import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import Swal from 'sweetalert2';
import { useScoring, useScoringDataMapper } from '../../hooks/useScoring';
import AccordionSection from '../../components/common/AccordionSection';
import { setScoringStatus } from '../../services/scoringService';
import {
  getEstadoCivilTexto,
  getGeneroTexto,
  getTipoIngresoTexto,
  getCategoriaFiscalTexto,
  getTipoPersonaTexto
} from '../../constants/scoringConstants';
const EvaluarExpediente = ({ user, onLogout, onNavigate }) => {
  const [anexoActual, setAnexoActual] = useState(1);
  const [documentos, setDocumentos] = useState([]);
  const [modalAprobar, setModalAprobar] = useState(false);
  const [modalRechazar, setModalRechazar] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [loading, setLoading] = useState(true);
  const [datosPlan, setDatosPlan] = useState(null);
  const [datosTitular, setDatosTitular] = useState(null);
  const [datosFiance, setDatosFiance] = useState(null);
  const [garanteData, setGaranteData] = useState(null);
  const [casoInfo, setCasoInfo] = useState(null);
  const [tipoPersona, setTipoPersona] = useState('');
  const [datosEmpresa, setDatosEmpresa] = useState(null);
  const [datosApoderado, setDatosApoderado] = useState(null);
  const [datosApoderadoConyuge, setDatosApoderadoConyuge] = useState(null);
  // Hooks de scoring
  const { loading: scoringLoading, loadScoringInfo } = useScoring();
  const { mapTitularData } = useScoringDataMapper();
  // Función para formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha || fecha === 'N/A') return 'N/A';
    try {
      if (fecha.includes('/')) {
        const partes = fecha.split('/');
        if (partes.length === 3) {
          const [dia, mes, anio] = partes;
          return `${dia}-${mes}-${anio}`;
        }
      }
      if (fecha.includes('-')) {
        const partes = fecha.split('-');
        if (partes.length === 3) {
          if (partes[0].length === 4) {
            const [anio, mes, dia] = partes;
            return `${dia}-${mes}-${anio}`;
          } else {
            return fecha;
          }
        }
      }
      return fecha;
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return fecha;
    }
  };
  // Formatear números como moneda
  const formatearMoneda = (valor) => {
    if (!valor) return 'N/A';
    const numero = typeof valor === 'string' ? parseFloat(valor.replace(/[^0-9.-]+/g, '')) : valor;
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(numero);
  };
  // Cargar datos del caso al montar el componente
  useEffect(() => {
    const loadCasoData = async () => {
      try {
        setLoading(true);
        // Leer datos de localStorage
        const casoDataStr = localStorage.getItem('casoData');
        if (!casoDataStr) {
          console.warn('⚠️ No hay datos del caso en localStorage');
          Swal.fire({
            icon: 'warning',
            title: 'Sin datos',
            text: 'No se encontraron datos del caso para evaluar.',
            confirmButtonColor: '#ef4444',
          });
          return;
        }
        const casoData = JSON.parse(casoDataStr);
        setCasoInfo(casoData);
        // Cargar información completa desde el API usando GetInfoStoredScoring
        const data = await loadScoringInfo(casoData.grupo, casoData.orden, 'STORED');
        if (data && data.titularData && data.planData) {
          const titularData = data.titularData;
          const planData = data.planData;

          // Leer garante del array warrantors (el backend devuelve un array)
          const garante = (data.warrantors && data.warrantors.length > 0) ? data.warrantors[0] : null;

          const garanteMapeado = garante
    ? {
        nombre: garante.warrantorFullName || 'N/A',
        dni: garante.warrantorDNI || 'N/A',
        fechaNacimiento: formatearFecha(garante.warrantorBirthDate),
        estadoCivil: getEstadoCivilTexto(garante.warrantorMarriageStatus),
        sexo: garante.warrantorGender || 'N/A',
        nacionalidad: garante.warrantorNationality || 'N/A',
        tipoIngreso: garante.warrantorIncomeType || 'N/A',
        ingresosNetos: formatearMoneda(garante.warrantorIncome),
        pep: garante.warrantorPEP === 'SI' || 'NO',

        //
        mail: garante.warrantorEmail || '',
        telefono: garante.warrantorPhone || '',
        nacionalidad: garante.warrantorCountry || '',
        categoriaFiscal: garante.warrantorTaxCondition || '',
        //

        // Datos del cónyuge del garante
        fianceCompleteName: garante.warrantorFianceCompleteName || '',
        fianceDocumentNumber: garante.warrantorFianceDocumentNumber || '',
        fianceBirthDate: formatearFecha(garante.warrantorFianceBirthDate),
        fianceGender: garante.warrantorFianceGender || '',
        fianceNationality: garante.warrantorFianceNationality || '',
        fiancePEP: garante.warrantorFiancePEP === 'SI' || '',
      }
    : null;

          setGaranteData(garanteMapeado);
          // Mapear datos del plan
          const planMapeado = {
            grupo: casoData.grupo || data.group || 'N/A',
            orden: casoData.orden || data.order || 'N/A',
            modelo: planData.productName || 'N/A',
            mesesPlan: planData.monthsPlan || 'N/A',
            concesionario: casoData.concesionario || planData.sellerHouse?.ceName || 'N/A',
            fechaAdjudicacion: formatearFecha(planData.adjDate),
            fechaAceptacion: formatearFecha(casoData.aceptacion || planData.acceptDate),
            tipoAdjudicacion: planData.adjType || 'N/A',
            saldoPagar: planData.debtValue || '0',
            cuotasVencer: planData.quotasLeft || '0',
            valorMovil: formatearMoneda(planData.productValue),
            valorCuota: planData.futureValue ? formatearMoneda(planData.futureValue) : '0',
            porcentajeDeuda: casoData.porcentajeDeuda || planData.debtPercent || '0'
          };
          // Mapear datos del titular
          
          const titularMapeado = {
            nombre: titularData.completeName || titularData.mainName || casoData.titular || 'N/A',
            dni: titularData.documentNumber || titularData.personNewconID || casoData.cuil || 'N/A',
            fechaNacimiento: formatearFecha(titularData.personBirthDate),
            estadoCivil: getEstadoCivilTexto(titularData.personMarriageStatus),
            nacionalidad: titularData.personNationality || 'N/A',
            sexo: getGeneroTexto(titularData.personGender),
            ingresosNetos: formatearMoneda(titularData.income),
            antiguedadLaboral: titularData.personlaborTime ? `${titularData.personlaborTime} años` : 'N/A',
            tipoIngreso: getTipoIngresoTexto(titularData.incomeType),
            categoriaFiscal: getCategoriaFiscalTexto(titularData.taxCategory),
            pep: titularData.pep || '',
            direccion: titularData.address || 'N/A',
            numero: titularData.addressNumber || '',
            pisoDpto: titularData.addressFloor || '',
            localidad: titularData.addressLocale || 'N/A',
            provincia: titularData.addressProvince || 'N/A',
            codigoPostal: titularData.addressPostalCode || 'N/A',
            telefono: titularData.phone || 'N/A',
            email: titularData.mail || 'N/A',
            nosisPep: titularData.nosisPEP || 'N/A',
            nosisLAFT: titularData.nosisLAFT || 'N/A',
            equifaxValues: titularData.equifaxValues || 'N/A',
            compliance_user_approve: titularData.compliance_user_approve,
            rci: planData.futureValue == 0 ? 0 : Math.round((titularData.income.replace(/\./g, '').replace(',', '.') / planData.futureValue) * 100) / 100
          }; 
          const fianceMapeado = {
            nombre: titularData.fianceCompleteName || 'N/A',
            dni: titularData.fianceDocumentNumber || 'N/A',
            fechaNacimiento: formatearFecha(titularData.fianceBirthDate),
            sexo: getGeneroTexto(titularData.fianceGender),
            nacionalidad: titularData.fianceNationality || 'N/A',
            pep: titularData.fiancePEP || '',
          };
          setDatosPlan(planMapeado);
          setDatosTitular(titularMapeado);
          setDatosFiance(fianceMapeado);
          // Determinar tipo de persona y mapear datos PJ
          const personType = titularData.personType || '';
          const isJur = personType === '2 - Persona Jurídica' || personType === '2';
          setTipoPersona(personType);
          if (isJur) {
            setDatosEmpresa({
              nombre: titularData.completeName || 'N/A',
              cuit: titularData.documentNumber || 'N/A',
              fechaConstitucion: formatearFecha(titularData.companyDate),
              tipoEmpresa: titularData.companyType || 'N/A',
              objetoEmpresa: titularData.companyPurpose || 'N/A',
              ingresos: formatearMoneda(titularData.income),
              pep: titularData.pep || '',
              telefono: titularData.phone || 'N/A',
              email: titularData.mail || 'N/A',
              direccion: titularData.address || 'N/A',
              numero: titularData.addressNumber || '',
              pisoDpto: titularData.addressFloor || '',
              localidad: titularData.addressLocale || 'N/A',
              provincia: titularData.addressProvince || 'N/A',
              codigoPostal: titularData.addressPostalCode || 'N/A',
            });
            if (data.apodData) {
              const apod = data.apodData;
              setDatosApoderado({
                nombre: apod.apodPersonCompleteName || 'N/A',
                dni: apod.apodPersonDocumentNumber || 'N/A',
                fechaNacimiento: formatearFecha(apod.apodPersonBirthDate),
                estadoCivil: apod.apodPersonMarriageStatus || 'N/A',
                sexo: apod.apodPersonGender || 'N/A',
                nacionalidad: apod.apodPersonNationality || 'N/A',
                direccion: apod.apodAddress || 'N/A',
                numero: apod.apodAddressNumber || '',
                pisoDpto: apod.apodAddressFloor || '',
                localidad: apod.apodAddressLocale || 'N/A',
                provincia: apod.apodAddressProvince || 'N/A',
                codigoPostal: apod.apodAddressPostalCode || 'N/A',
              });
              if (apod.apodFianceCompleteName) {
                setDatosApoderadoConyuge({
                  nombre: apod.apodFianceCompleteName,
                  dni: apod.apodFianceDocumentNumber || 'N/A',
                  fechaNacimiento: formatearFecha(apod.apodFianceBirthDate),
                  sexo: apod.apodFianceGender || 'N/A',
                  nacionalidad: apod.apodFianceNationality || 'N/A',
                });
              }
            }
          }
          // Almacenar documentos si existen
          if (data.scoringDocuments && Array.isArray(data.scoringDocuments)) {
            setDocumentos(data.scoringDocuments);
            console.log('📄 Documentos cargados:', data.scoringDocuments.length);
          } else {
            console.warn('⚠️ No se encontraron documentos en la respuesta');
            setDocumentos([]);
          }
        }
      } catch (error) {
        console.error('Error cargando datos del caso:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los datos del caso.',
          confirmButtonColor: '#ef4444',
        });
      } finally {
        setLoading(false);
      }
    };
    loadCasoData();
  }, []);
  const handleAnexoAnterior = () => {
    if (anexoActual > 1) {
      setAnexoActual(anexoActual - 1);
    }
  };
  const handleAnexoSiguiente = () => {
    if (anexoActual < documentos.length) {
      setAnexoActual(anexoActual + 1);
    }
  };

  // Obtener el documento actual
  const documentoActual = documentos.length > 0 ? documentos[anexoActual - 1] : null;
  const handleAprobar = () => {
    setModalAprobar(true);
  };
  const handleConfirmarAprobar = async () => {
    setModalAprobar(false);

    // Validar que tengamos el ID del scoring
    if (!casoInfo || !casoInfo.idScoring) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontró el ID del expediente.',
        confirmButtonColor: '#ef4444',
      });
      return;
    }

    try {
      setLoading(true);

      // Llamar al endpoint para cambiar el estado a FINALIZADO APTO
      const userID = user?.id || user?.userId || 0;

      
      //COMPLIANCE
      if(user.group_ID === 4) {  await setScoringStatus(casoInfo.idScoring, 'REVISION ESPECIAL COMPLETADA', '', userID);  } 
      
      //NORMAL
      else { await setScoringStatus(casoInfo.idScoring, 'FINALIZADO APTO', '', userID); }


      // SweetAlert de aprobación exitosa
      await Swal.fire({
        title: '¡Expediente Aprobado!',
        text: 'El expediente ha sido aprobado correctamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#ef4444'
      });

      // Navegar al menú principal
      if (onNavigate) {
        onNavigate('home');
      }

    } catch (error) {
      console.error('Error al aprobar expediente:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error al Aprobar',
        text: error.message || 'No se pudo aprobar el expediente. Por favor, intente nuevamente.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setLoading(false);
    }
  };
  const handleRechazar = () => {
    setModalRechazar(true);
  };
  const handleConfirmarRechazar = async () => {
    // Validar que el motivo de rechazo no esté vacío
    if (!motivoRechazo || motivoRechazo.trim() === '') {
      await Swal.fire({
        icon: 'warning',
        title: 'Motivo requerido',
        text: 'Debe ingresar el motivo del rechazo antes de continuar.',
        confirmButtonColor: '#ef4444',
      });
      return;
    }

    setModalRechazar(false);

    // Validar que tengamos el ID del scoring
    if (!casoInfo || !casoInfo.idScoring) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se encontró el ID del expediente.',
        confirmButtonColor: '#ef4444',
      });
      return;
    }

    try {
      setLoading(true);

      // Llamar al endpoint para cambiar el estado a RECHAZADO con el motivo
      const userID = user?.id || user?.userId || 0;
      
      //COMPLIANCE
      if(user.group_ID === 4) { await setScoringStatus(casoInfo.idScoring, 'ANULADO', motivoRechazo.trim(), userID); } 

      //NORMAL
      else {await setScoringStatus(casoInfo.idScoring, 'RECHAZADO', motivoRechazo.trim(), userID);}


      // SweetAlert de rechazo exitoso
      await Swal.fire({
        title: 'Expediente Rechazado',
        text: 'El expediente ha sido rechazado correctamente.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#ef4444'
      });

      setMotivoRechazo('');

      // Navegar al menú principal
      if (onNavigate) {
        onNavigate('home');
      }

    } catch (error) {
      console.error('Error al rechazar expediente:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error al Rechazar',
        text: error.message || 'No se pudo rechazar el expediente. Por favor, intente nuevamente.',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setLoading(false);
    }
  };
  const isJuridica = tipoPersona === '2 - Persona Jurídica' || tipoPersona === '2';
  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-4 py-4">
        {/* Título */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">
              Evaluar Expediente <span style={{ fontSize: '16px' }}>{user.group_ID === 4 ? '| Honda Compliance' : ''}</span>
            </h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Revise y evalúe la documentación del expediente de análisis crediticio <b>{user.group_ID === 4 ? 'Compliance' : ''}</b></p>
        </div>
        {/* Loader mientras carga */}
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12">
            <HondaLoader
              message="Cargando datos del expediente..."
              variant="inline"
              size="large"
              subtitle="Obteniendo información completa del caso"
            />
          </div>
        ) : !datosPlan || !datosTitular ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
            <span className="material-icons text-5xl text-gray-300 mb-3">error_outline</span>
            <p className="text-gray-400">No se pudieron cargar los datos del expediente</p>
            <p className="text-gray-300 text-sm mt-1">
              Por favor, intente nuevamente
            </p>
          </div>
        ) : (
          <>{/* Contenido principal: dos columnas */}
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Columna izquierda - Visualización de documentos */}
              <div className="flex-1 bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">
                {/* Barra superior negra */}
                <div className="h-2 bg-gray-800"></div>
                {/* Cabecera con navegación de anexos - NEGRO */}
                <div className="bg-gray-800 px-3 py-2 border-b border-gray-700">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <div className="flex items-center gap-4">
                      <p className="text-xs text-white">
                        <span className="font-bold">Nombre del Anexo:</span> {documentoActual?.documentName || 'Sin documento'}
                      </p>
                      <p className="text-xs text-white">
                        <span className="font-bold">Anexo:</span> {anexoActual} / {documentos.length}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAnexoAnterior}
                        disabled={anexoActual === 1 || documentos.length === 0}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <span className="material-icons text-sm">chevron_left</span>
                        Anterior
                      </button>
                      <button
                        onClick={handleAnexoSiguiente}
                        disabled={anexoActual === documentos.length || documentos.length === 0}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Siguiente
                        <span className="material-icons text-sm">chevron_right</span>
                      </button>
                    </div>
                  </div>
                </div>
                {/* Visor PDF */}
                <div className="flex-1 p-3 flex items-center justify-center bg-gray-100" style={{ minHeight: '550px' }}>
                  {documentos.length === 0 ? (
                    <div className="text-center">
                      <span className="material-icons text-5xl text-gray-400 mb-2">description</span>
                      <p className="text-sm text-gray-500">No hay documentos disponibles</p>
                      <p className="text-xs text-gray-400 mt-1">Este expediente no tiene documentos adjuntos</p>
                    </div>
                  ) : documentoActual ? (
                    <iframe
                      src={documentoActual.documentPath}
                      title={documentoActual.documentName}
                      className="w-full h-full rounded-lg border border-gray-300"
                      style={{ minHeight: '520px' }}
                    />
                  ) : (
                    <div className="text-center">
                      <span className="material-icons text-5xl text-gray-400 mb-2">picture_as_pdf</span>
                      <p className="text-sm text-gray-500">Vista previa del documento</p>
                      <p className="text-xs text-gray-400 mt-1">Seleccione un documento para visualizar</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Columna derecha - Datos del Plan y Titular (35%) */}
              <div className="w-full lg:w-[35%] bg-white border-2 border-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col">
                {/* Barra superior negra */}
                <div className="h-2 bg-gray-800"></div>
                {/* Nro de Expediente con botones de acción - BANNER NEGRO */}
                <div className="px-3 py-2 border-b border-gray-700 bg-gray-800 flex items-center justify-between">
                  <p className="text-xs font-bold text-white">Nro de Expediente Scoring: {casoInfo?.idScoring || 'N/A'}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleRechazar}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition text-xs font-medium shadow-sm"
                    >
                      Rechazar
                    </button>
                    <button
                      onClick={handleAprobar}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md transition text-xs font-medium shadow-sm"
                    >
                      Aprobar
                    </button>
                  </div>
                </div>
                {/* Contenido con scroll - ajustado a la altura del visor */}
                <div className="flex-1 overflow-y-auto" style={{ maxHeight: '550px' }}>
                  {/* DATOS DEL PLAN */}
                  <AccordionSection title="DATOS DEL PLAN" icon="description">
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Grupo</span>
                      <span className="text-xs font-medium text-gray-900">{datosPlan.grupo}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Orden</span>
                      <span className="text-xs font-medium text-gray-900">{datosPlan.orden}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Modelo Suscripto</span>
                      <span className="text-xs text-gray-800 text-right">{datosPlan.modelo}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Meses Plan</span>
                      <span className="text-xs text-gray-800">{datosPlan.mesesPlan}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Concesionario</span>
                      <span className="text-xs text-gray-800 text-right">{datosPlan.concesionario}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">F. Adjudicación</span>
                      <span className="text-xs text-gray-800">{datosPlan.fechaAdjudicacion}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">F. Aceptación</span>
                      <span className="text-xs text-gray-800">{datosPlan.fechaAceptacion}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Tipo Adjudicación</span>
                      <span className="text-xs text-gray-800 text-right">{datosPlan.tipoAdjudicacion}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Saldo a Pagar</span>
                      <span className="text-xs font-medium text-gray-900">AR$ {datosPlan.saldoPagar}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Cuotas a Vencer</span>
                      <span className="text-xs text-gray-800">{datosPlan.cuotasVencer}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Valor Móvil</span>
                      <span className="text-xs text-gray-800">{datosPlan.valorMovil}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Valor Cuota</span>
                      <span className="text-xs text-gray-800">{datosPlan.valorCuota}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-xs text-gray-500">Porcentaje Deuda</span>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${parseFloat(datosPlan.porcentajeDeuda) > 66 ? 'bg-red-100 text-red-700' :
                          parseFloat(datosPlan.porcentajeDeuda) > 33 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                        }`}>
                        {datosPlan.porcentajeDeuda}%
                      </span>
                    </div>
                  </AccordionSection>

                  {/* DATOS DEL TITULAR - solo Persona Física */}
                  {!isJuridica && (
                    <AccordionSection title="DATOS DEL TITULAR" icon="person">
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Nombre Completo</span>
                      <span className="text-xs font-medium text-gray-900 text-right">{datosTitular.nombre}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">DNI</span>
                      <span className="text-xs text-gray-800">{datosTitular.dni}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Fecha Nacimiento</span>
                      <span className="text-xs text-gray-800">{datosTitular.fechaNacimiento}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Estado Civil</span>
                      <span className="text-xs text-gray-800">{datosTitular.estadoCivil}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Sexo</span>
                      <span className="text-xs text-gray-800">{datosTitular.sexo || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Nacionalidad</span>
                      <span className="text-xs text-gray-800">{datosTitular.nacionalidad}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Tipo de Ingreso</span>
                      <span className="text-xs text-gray-800">{datosTitular.tipoIngreso || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Ingresos Netos</span>
                      <span className="text-xs font-medium text-gray-900">{datosTitular.ingresosNetos}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Antigüedad Laboral</span>
                      <span className="text-xs text-gray-800">{datosTitular.antiguedadLaboral}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Categoría Fiscal</span>
                      <span className="text-xs text-gray-800">{datosTitular.categoriaFiscal || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">PEP (Persona Expuesta Políticamente)</span>
                      <span className={`text-xs font-medium ${datosTitular.pep === 'SI' ? 'text-red-600' : 'text-green-600'}`}>
                        {datosTitular.pep || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Dirección</span>
                      <span className="text-xs text-gray-800 text-right">
                        {datosTitular.direccion}
                        {datosTitular.numero && ` ${datosTitular.numero}`}
                        {datosTitular.pisoDpto && ` - ${datosTitular.pisoDpto}`}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Código Postal</span>
                      <span className="text-xs text-gray-800">{datosTitular.codigoPostal || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Localidad</span>
                      <span className="text-xs text-gray-800">{datosTitular.localidad}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Provincia</span>
                      <span className="text-xs text-gray-800">{datosTitular.provincia}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-xs text-gray-500">Teléfono</span>
                      <span className="text-xs text-gray-800">{datosTitular.telefono || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-xs text-gray-500">Email</span>
                      <span className="text-xs text-gray-800 text-right break-all">{datosTitular.email || 'N/A'}</span>
                    </div>
                    </AccordionSection>
                  )}

                  {/* DATOS DEL CÓNYUGE - solo Persona Física */}
                  {!isJuridica && datosFiance && datosFiance.nombre !== 'N/A' && (
                    <AccordionSection title="DATOS DEL CÓNYUGE" icon="people">
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Nombre Completo</span>
                        <span className="text-xs text-gray-900">{datosFiance.nombre}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">DNI</span>
                        <span className="text-xs text-gray-900">{datosFiance.dni}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Fecha Nacimiento</span>
                        <span className="text-xs text-gray-900">{datosFiance.fechaNacimiento}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Sexo</span>
                        <span className="text-xs text-gray-900">{datosFiance.sexo}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Nacionalidad</span>
                        <span className="text-xs text-gray-900">{datosFiance.nacionalidad}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-xs text-gray-500">PEP</span>
                        <span className="text-xs text-gray-900">{datosFiance.pep}</span>
                      </div>
                    </AccordionSection>
                  )}

                  {/* DATOS DE LA EMPRESA - solo Persona Jurídica */}
                  {isJuridica && datosEmpresa && (
                    <AccordionSection title="DATOS DE LA EMPRESA" icon="business">
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Nombre de la Empresa</span>
                        <span className="text-xs font-medium text-gray-900 text-right">{datosEmpresa.nombre}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">CUIT</span>
                        <span className="text-xs text-gray-800">{datosEmpresa.cuit}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Fecha Constitución</span>
                        <span className="text-xs text-gray-800">{datosEmpresa.fechaConstitucion}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Tipo de Empresa</span>
                        <span className="text-xs text-gray-800">{datosEmpresa.tipoEmpresa}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Objeto Social</span>
                        <span className="text-xs text-gray-800 text-right">{datosEmpresa.objetoEmpresa}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Ingresos</span>
                        <span className="text-xs font-medium text-gray-900">{datosEmpresa.ingresos}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">PEP</span>
                        <span className={`text-xs font-medium ${datosEmpresa.pep === 'SI' ? 'text-red-600' : 'text-green-600'}`}>{datosEmpresa.pep}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Teléfono</span>
                        <span className="text-xs text-gray-800">{datosEmpresa.telefono}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Email</span>
                        <span className="text-xs text-gray-800 text-right break-all">{datosEmpresa.email}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Dirección</span>
                        <span className="text-xs text-gray-800 text-right">
                          {datosEmpresa.direccion}
                          {datosEmpresa.numero && ` ${datosEmpresa.numero}`}
                          {datosEmpresa.pisoDpto && ` - ${datosEmpresa.pisoDpto}`}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Código Postal</span>
                        <span className="text-xs text-gray-800">{datosEmpresa.codigoPostal}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Localidad</span>
                        <span className="text-xs text-gray-800">{datosEmpresa.localidad}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-xs text-gray-500">Provincia</span>
                        <span className="text-xs text-gray-800">{datosEmpresa.provincia}</span>
                      </div>
                    </AccordionSection>
                  )}

                  {/* DATOS DEL APODERADO - solo Persona Jurídica */}
                  {isJuridica && datosApoderado && (
                    <AccordionSection title="DATOS DEL APODERADO" icon="person_pin">
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Nombre Completo</span>
                        <span className="text-xs font-medium text-gray-900 text-right">{datosApoderado.nombre}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">DNI</span>
                        <span className="text-xs text-gray-800">{datosApoderado.dni}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Fecha Nacimiento</span>
                        <span className="text-xs text-gray-800">{datosApoderado.fechaNacimiento}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Estado Civil</span>
                        <span className="text-xs text-gray-800">{datosApoderado.estadoCivil}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Género</span>
                        <span className="text-xs text-gray-800">{datosApoderado.sexo}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Nacionalidad</span>
                        <span className="text-xs text-gray-800">{datosApoderado.nacionalidad}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Dirección</span>
                        <span className="text-xs text-gray-800 text-right">
                          {datosApoderado.direccion}
                          {datosApoderado.numero && ` ${datosApoderado.numero}`}
                          {datosApoderado.pisoDpto && ` - ${datosApoderado.pisoDpto}`}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Código Postal</span>
                        <span className="text-xs text-gray-800">{datosApoderado.codigoPostal}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Localidad</span>
                        <span className="text-xs text-gray-800">{datosApoderado.localidad}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-xs text-gray-500">Provincia</span>
                        <span className="text-xs text-gray-800">{datosApoderado.provincia}</span>
                      </div>
                    </AccordionSection>
                  )}

                  {/* CÓNYUGE DEL APODERADO - solo Persona Jurídica */}
                  {isJuridica && datosApoderadoConyuge && (
                    <AccordionSection title="CÓNYUGE DEL APODERADO" icon="people">
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Nombre Completo</span>
                        <span className="text-xs font-medium text-gray-900 text-right">{datosApoderadoConyuge.nombre}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">DNI</span>
                        <span className="text-xs text-gray-800">{datosApoderadoConyuge.dni}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Fecha Nacimiento</span>
                        <span className="text-xs text-gray-800">{datosApoderadoConyuge.fechaNacimiento}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-xs text-gray-500">Sexo</span>
                        <span className="text-xs text-gray-800">{datosApoderadoConyuge.sexo}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-xs text-gray-500">Nacionalidad</span>
                        <span className="text-xs text-gray-800">{datosApoderadoConyuge.nacionalidad}</span>
                      </div>
                    </AccordionSection>
                  )}

                  {/* DATOS DEL GARANTE */}
                  {garanteData && (
                    <AccordionSection title="DATOS DEL GARANTE" icon="verified_user">
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Nombre Completo</span>
                          <span className="text-xs font-medium text-gray-900 text-right">{garanteData.nombre}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">DNI</span>
                          <span className="text-xs text-gray-800">{garanteData.dni}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Fecha Nacimiento</span>
                          <span className="text-xs text-gray-800">{garanteData.fechaNacimiento}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Estado Civil</span>
                          <span className="text-xs text-gray-800">{garanteData.estadoCivil}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Sexo</span>
                          <span className="text-xs text-gray-800">{garanteData.sexo}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Nacionalidad</span>
                          <span className="text-xs text-gray-800">{garanteData.nacionalidad}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Tipo de Ingreso</span>
                          <span className="text-xs text-gray-800">{garanteData.tipoIngreso}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Ingresos Netos</span>
                          <span className="text-xs font-medium text-gray-900">{garanteData.ingresosNetos}</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-xs text-gray-500">PEP</span>
                          <span className={`text-xs font-medium ${garanteData.pep === 'SI' ? 'text-red-600' : 'text-green-600'}`}>
                            {garanteData.pep}
                          </span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-xs text-gray-500">Mail</span>
                          <span className={`text-xs font-medium`}>
                            {garanteData.mail}
                          </span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-xs text-gray-500">Teléfono</span>
                          <span className={`text-xs font-medium`}>
                            {garanteData.telefono}
                          </span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-xs text-gray-500">Categoria Fiscal</span>
                          <span className={`text-xs font-medium `}>
                            {garanteData.categoriaFiscal}
                          </span>
                        </div>
                      {/* CÓNYUGE DEL GARANTE */}
                      {garanteData.fianceCompleteName && garanteData.fianceCompleteName !== 'N/A' && garanteData.fianceCompleteName !== '' && (
                        <AccordionSection title="CÓNYUGE DEL GARANTE" icon="people">
                            <div className="flex justify-between py-1 border-b border-gray-100">
                              <span className="text-xs text-gray-500">Nombre Completo</span>
                              <span className="text-xs font-medium text-gray-900 text-right">{garanteData.fianceCompleteName}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-100">
                              <span className="text-xs text-gray-500">DNI</span>
                              <span className="text-xs text-gray-800">{garanteData.fianceDocumentNumber}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-100">
                              <span className="text-xs text-gray-500">Fecha Nacimiento</span>
                              <span className="text-xs text-gray-800">{garanteData.fianceBirthDate}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-100">
                              <span className="text-xs text-gray-500">Sexo</span>
                              <span className="text-xs text-gray-800">{garanteData.fianceGender}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-100">
                              <span className="text-xs text-gray-500">Nacionalidad</span>
                              <span className="text-xs text-gray-800">{garanteData.fianceNationality}</span>
                            </div>
                            <div className="flex justify-between py-1">
                              <span className="text-xs text-gray-500">PEP</span>
                              <span className={`text-xs font-medium ${garanteData.fiancePEP === 'SI' ? 'text-red-600' : 'text-green-600'}`}>
                                {garanteData.fiancePEP}
                              </span>
                            </div>
                        </AccordionSection>
                      )}
                    </AccordionSection>                    
                   )}

                    {/* EQUIFAX */}
                    {(
                      <AccordionSection title="DATOS EQUIFAX" icon="account_balance">

                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">RCI</span>
                          <span className="text-xs font-medium text-gray-900 text-right">{datosTitular.rci}</span>
                        </div>

                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Nodo</span>
                          <span className="text-xs font-medium text-gray-900 text-right">{datosTitular.equifaxValues?.split('|')[0]?.trim() || 'N/A'}</span>
                        </div>

                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Población de Score</span>
                          <span className="text-xs font-medium text-gray-900 text-right">{datosTitular.equifaxValues?.split('|')[1]?.trim() || 'N/A'}</span>
                        </div>

                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Score de Riesgo</span>
                          <span className="text-xs font-medium text-gray-900 text-right">{datosTitular.equifaxValues?.split('|')[2]?.trim() || 'N/A'}</span>
                        </div>
                        
                        
                      </AccordionSection>
                    )}

                    {/* NOSIS */}
                    {(
                      <AccordionSection title="DATOS NOSIS" icon="business">
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">API PEP</span>
                          <span className="text-xs font-medium text-gray-900 text-right">{datosTitular.nosisPep}</span>
                        </div>

                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">API LAFT</span>
                          <span className="text-xs font-medium text-gray-900 text-right">{datosTitular.nosisLAFT}</span>
                        </div>
                        
                      </AccordionSection>
                    )}
                    {/* APROBACION COMPLINCE */}
                    {datosTitular.compliance_user_approve !== '' && (
                     
                      <AccordionSection title="DATOS COMPLIANCE" icon="business">
                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Status Compliance</span>
                          <span className="text-xs font-medium text-gray-900 text-right">Aprobado</span>
                        </div>

                        <div className="flex justify-between py-1 border-b border-gray-100">
                          <span className="text-xs text-gray-500">Usuario Compliance</span>
                          <span className="text-xs font-medium text-gray-900 text-right">{/*datosTitular.compliance_user_approve*/} prueba_2_compliance</span>
                        </div>
                        
                      </AccordionSection>
                    )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Modal Aprobar Expediente */}
      {modalAprobar && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setModalAprobar(false)}></div>
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scale-in">
              {/* Header con gradiente verde */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 rounded-t-2xl shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <span className="material-icons text-white text-xl">check_circle</span>
                    </div>
                    <h2 className="text-white text-lg font-bold tracking-wide">APROBAR EXPEDIENTE</h2>
                  </div>
                  <button onClick={() => setModalAprobar(false)} className="text-white/80 hover:text-white hover:rotate-90 transition-all duration-300 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10">
                    <span className="material-icons text-xl">close</span>
                  </button>
                </div>
              </div>
              <div className="p-6">
                {/* Info del expediente */}
                <div className="mb-5 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="material-icons text-green-600 text-sm">folder</span>
                      <p className="text-gray-700 text-sm">Expediente N°: <span className="font-bold text-gray-900">{casoInfo?.idScoring || 'N/A'}</span></p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="material-icons text-green-600 text-sm">person</span>
                      <p className="text-gray-700 text-sm">Titular: <span className="font-bold text-gray-900">{datosTitular?.nombre || 'N/A'}</span></p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="material-icons text-green-600 text-sm">tag</span>
                      <p className="text-gray-700 text-sm">Grupo/Orden: <span className="font-bold text-gray-900">{datosPlan?.grupo || 'N/A'} / {datosPlan?.orden || 'N/A'}</span></p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-5 p-3 bg-green-50 rounded-xl border border-green-200 flex items-start gap-2">
                  <span className="material-icons text-green-600 text-lg">info</span>
                  <span>¿Está seguro que desea aprobar este expediente?</span>
                </p>
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button onClick={() => setModalAprobar(false)} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 text-sm font-semibold hover:shadow-md">
                    Cancelar
                  </button>
                  <button onClick={handleConfirmarAprobar} className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:shadow-lg text-white rounded-xl transition-all duration-300 text-sm font-semibold flex items-center gap-2">
                    <span className="material-icons text-lg">check_circle</span>
                    Aprobar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal Rechazar Expediente */}
      {modalRechazar && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setModalRechazar(false)}></div>
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scale-in">
              {/* Header con gradiente rojo */}
              <div className="gradient-red px-6 py-4 rounded-t-2xl shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <span className="material-icons text-white text-xl">cancel</span>
                    </div>
                    <h2 className="text-white text-lg font-bold tracking-wide">RECHAZAR EXPEDIENTE</h2>
                  </div>
                  <button onClick={() => setModalRechazar(false)} className="text-white/80 hover:text-white hover:rotate-90 transition-all duration-300 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10">
                    <span className="material-icons text-xl">close</span>
                  </button>
                </div>
              </div>
              <div className="p-6">
                {/* Info del expediente */}
                <div className="mb-5 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="material-icons text-honda-500 text-sm">folder</span>
                      <p className="text-gray-700 text-sm">Expediente N°: <span className="font-bold text-gray-900">{casoInfo?.idScoring || 'N/A'}</span></p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="material-icons text-honda-500 text-sm">person</span>
                      <p className="text-gray-700 text-sm">Titular: <span className="font-bold text-gray-900">{datosTitular?.nombre || 'N/A'}</span></p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="material-icons text-honda-500 text-sm">tag</span>
                      <p className="text-gray-700 text-sm">Grupo/Orden: <span className="font-bold text-gray-900">{datosPlan?.grupo || 'N/A'} / {datosPlan?.orden || 'N/A'}</span></p>
                    </div>
                  </div>
                </div>
                {/* Motivo del rechazo */}
                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="material-icons text-honda-600 text-lg">description</span>
                    Motivo del rechazo:
                  </label>
                  <textarea
                    value={motivoRechazo}
                    onChange={(e) => setMotivoRechazo(e.target.value)}
                    rows="4"
                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-honda-100 focus:border-honda-500 transition-all duration-300 resize-none"
                    placeholder="Describa detalladamente el motivo del rechazo..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <span className="material-icons text-xs">info</span>
                    Este motivo será registrado en el historial del expediente
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button onClick={() => setModalRechazar(false)} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 text-sm font-semibold hover:shadow-md">
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmarRechazar}
                    className="px-5 py-2.5 gradient-red hover:shadow-glow-red text-white rounded-xl transition-all duration-300 text-sm font-semibold flex items-center gap-2"
                  >
                    <span className="material-icons text-lg">cancel</span>
                    Rechazar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </MainLayout>
  );
};
export default EvaluarExpediente;

